import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import UserMenu from './UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoogleDriveSetup = () => {
  const { user } = useAuth();
  const [authStatus, setAuthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [driveFiles, setDriveFiles] = useState([]);
  const [oauthResult, setOauthResult] = useState(null);

  useEffect(() => {
    checkAuthStatus();
    
    // Check for OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const authResult = urlParams.get('auth');
    const errorMessage = urlParams.get('message');
    
    if (authResult === 'success') {
      setOauthResult({ success: true, message: 'Google Drive успешно подключен!' });
    } else if (authResult === 'error') {
      setOauthResult({ 
        success: false, 
        message: errorMessage || 'Ошибка при подключении Google Drive' 
      });
    }
    
    // Clear URL parameters
    if (authResult) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/drive/auth/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAuthStatus(response.data);
    } catch (error) {
      console.error('Failed to check auth status:', error);
      setAuthStatus({ authenticated: false, message: 'Не удалось проверить статус' });
    } finally {
      setLoading(false);
    }
  };

  const initiateOAuth = async () => {
    try {
      setInitializing(true);
      
      // Get OAuth authorization URL
      const response = await axios.get(`${API}/drive/auth/oauth/url`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data?.oauth_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.oauth_url;
      } else {
        throw new Error('No OAuth URL received');
      }
      
    } catch (error) {
      console.error('Failed to initiate OAuth:', error);
      alert('Ошибка при инициализации OAuth: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
      setInitializing(false);
    }
  };

  const initiateAuth = async () => {
    try {
      setInitializing(true);
      await axios.post(`${API}/drive/auth/initiate`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Check status again after auth
      await checkAuthStatus();
      
      alert('Google Drive успешно подключен! Теперь можно загружать фотографии в проекты.');
      
    } catch (error) {
      console.error('Failed to initiate auth:', error);
      alert('Ошибка при подключении Google Drive: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    } finally {
      setInitializing(false);
    }
  };

  const loadDriveFiles = async () => {
    try {
      const response = await axios.get(`${API}/drive/files?limit=20`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDriveFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load Drive files:', error);
      alert('Не удалось загрузить файлы из Google Drive');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <UserMenu />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Доступ запрещён</h1>
            <p className="text-gray-600">Только администраторы могут настраивать Google Drive интеграцию.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <UserMenu />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center mb-6">
              <svg className="w-8 h-8 mr-3 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
              </svg>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Google Drive Интеграция</h1>
                <p className="text-gray-600">Настройка подключения для загрузки фотографий проектов</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Проверка подключения...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* OAuth Result Notification */}
                {oauthResult && (
                  <div className={`rounded-lg p-4 mb-4 ${oauthResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-start">
                      <svg className={`w-5 h-5 mt-0.5 mr-3 ${oauthResult.success ? 'text-green-500' : 'text-red-500'}`} fill="currentColor" viewBox="0 0 20 20">
                        {oauthResult.success ? (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        )}
                      </svg>
                      <div>
                        <h4 className={`text-sm font-semibold mb-1 ${oauthResult.success ? 'text-green-800' : 'text-red-800'}`}>
                          {oauthResult.success ? 'Успешное подключение' : 'Ошибка подключения'}
                        </h4>
                        <p className={`text-sm ${oauthResult.success ? 'text-green-700' : 'text-red-700'}`}>
                          {oauthResult.message}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setOauthResult(null)}
                      className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Закрыть
                    </button>
                  </div>
                )}
                  {!authStatus?.authenticated && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-500 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="text-sm font-semibold text-yellow-800 mb-1">Google Drive не подключен</h4>
                          <p className="text-sm text-yellow-700">
                            Необходимо настроить аутентификацию Google Drive для загрузки файлов в проекты.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* OAuth Authorization Section */}
                  {!authStatus?.authenticated && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-blue-900 mb-2">OAuth Аутентификация (Рекомендуется)</h3>
                          <p className="text-blue-800 mb-4">
                            Подключитесь к Google Drive через OAuth для безопасной интеграции с вашим Google аккаунтом.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={initiateOAuth}
                        disabled={initializing}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        {initializing ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Подключение...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                            </svg>
                            Подключиться через Google
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {authStatus?.authenticated && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="text-sm font-semibold text-green-800 mb-1">Google Drive подключен</h4>
                          <p className="text-sm text-green-700">
                            Google Drive интеграция активна. Пользователи могут загружать файлы и фотографии в проекты.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={checkAuthStatus}
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Обновить статус
                  </button>

                  {authStatus?.authenticated && (
                    <button
                      onClick={loadDriveFiles}
                      className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Показать файлы в Drive
                    </button>
                  )}
                </div>

                {/* Instructions */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <details className="mb-4">
                    <summary className="cursor-pointer text-lg font-semibold text-gray-900 mb-3 hover:text-blue-600">
                      📋 Альтернативный способ: Service Account (для продвинутых пользователей)
                    </summary>
                    
                    <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                      <div className="flex">
                        <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm text-blue-700">
                          <p className="font-medium">Service Account настройка</p>
                          <p className="mt-1">Для корпоративного развертывания можно использовать Service Account вместо OAuth.</p>
                        </div>
                      </div>
                    </div>
                    
                    <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                      <li>
                        <strong>Google Cloud Console:</strong>
                        <div className="ml-6 mt-1 text-gray-600">
                          • Перейдите на <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">console.cloud.google.com</a><br/>
                          • Создайте проект или выберите существующий<br/>
                          • Включите Google Drive API в разделе "APIs & Services"
                        </div>
                      </li>
                      
                      <li>
                        <strong>Создание Service Account:</strong>
                        <div className="ml-6 mt-1 text-gray-600">
                          • Перейдите "IAM & Admin" → "Service Accounts"<br/>
                          • Нажмите "Create Service Account"<br/>
                          • Название: <code className="bg-gray-100 px-1 rounded">profland-crm-drive</code>
                        </div>
                      </li>
                      
                      <li>
                        <strong>Создание ключа:</strong>
                        <div className="ml-6 mt-1 text-gray-600">
                          • В созданном Service Account нажмите "Keys" → "Add Key"<br/>
                          • Выберите формат JSON и скачайте файл<br/>
                          • Сохраните как <code className="bg-gray-100 px-1 rounded">service_account.json</code> в папку backend
                        </div>
                      </li>
                      
                      <li>
                        <strong>Перезапуск сервиса:</strong>
                        <div className="ml-6 mt-1 text-gray-600">
                          • Перезапустите backend сервис<br/>
                          • Service Account будет автоматически подключен
                        </div>
                      </li>
                    </ol>
                    
                    <div className="mt-4 p-4 bg-green-50 rounded-md">
                      <h4 className="text-sm font-semibold text-green-900 mb-2">📁 Рекомендация для компании:</h4>
                      <p className="text-sm text-green-800">
                        Создайте корпоративную папку "Profland CRM Files" в Google Drive и предоставьте 
                        доступ Service Account (email будет указан в скачанном JSON файле).
                      </p>
                    </div>
                    
                    <div className="mt-4 p-4 bg-yellow-50 rounded-md">
                      <p className="text-sm text-yellow-800">
                        <strong>Важно:</strong> Service Account файл содержит секретные данные. 
                        Храните его в безопасном месте и регулярно обновляйте ключи.
                      </p>
                    </div>
                  </details>

                  {/* OAuth Setup Information */}
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">ℹ️ Информация об OAuth аутентификации</h3>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p>• <strong>Безопасность:</strong> OAuth обеспечивает безопасный доступ без раскрытия паролей</p>
                      <p>• <strong>Простота:</strong> Одна кнопка для подключения к Google Drive</p>
                      <p>• <strong>Управление доступом:</strong> Можно отозвать доступ в любое время через Google аккаунт</p>
                      <p>• <strong>Автоматическое обновление:</strong> Токены обновляются автоматически</p>
                    </div>
                  </div>
                </div>

                {/* Drive Files List */}
                {driveFiles.length > 0 && (
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Последние файлы в Google Drive</h3>
                    <div className="space-y-2">
                      {driveFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <div>
                              <p className="font-medium text-gray-900">{file.name}</p>
                              <p className="text-sm text-gray-500">{file.mimeType}</p>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(file.createdTime).toLocaleDateString('ru-RU')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleDriveSetup;